<template>
  <div class="px-2">
    <vue-element-loading
      :active="appLoading"
      :is-full-screen="true"
      background-color="#FFFFFF"
      color="#9E6D2B"
      spinner="bar-fade-scale"
    />
    <ServerError v-if="ServerError" />
    <v-snackbar v-model="showsnackbar" color="#9E6D2B" right :timeout="timeout">
      <span style="color: #FFFFFF; font-weight: 600;">{{ msg }}</span>
      <template v-slot:action="{ attrs }">
        <v-btn text color="#FFFFFF" v-bind="attrs" @click="showsnackbar = false">
          <v-icon style="color: #FFFFFF">mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>

    <!-- TOP HEADER & CONTROLS -->
    <v-row align="center" justify="space-between" class="mb-3">
      <v-col cols="12" sm="6" md="6" class="py-1">
        <span class="mainHeader">Stock Summary</span>
      </v-col>
      <v-col cols="12" sm="6" md="6" class="d-flex align-center justify-end py-1" style="gap: 12px;">
        <!-- Date Picker -->
        <div style="width: 170px;">
          <v-menu
            ref="menu"
            v-model="menu"
            :close-on-content-click="false"
            :return-value.sync="date"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="date"
                label="Date"
                outlined
                dense
                readonly
                hide-details
                background-color="#FFFFFF"
                v-bind="attrs"
                v-on="on"
                prepend-inner-icon="mdi-calendar"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="date"
              no-title
              scrollable
              color="#9E6D2B"
              @change="$refs.menu.save(date); fetchData()"
            >
              <v-spacer></v-spacer>
            </v-date-picker>
          </v-menu>
        </div>

        <!-- Download PDF Button -->
        <v-btn
          color="#9E6D2B"
          dark
          height="40"
          elevation="1"
          class="text-none font-weight-bold px-4"
          style="border-radius: 8px;"
          @click="downloadPDF"
          :disabled="appLoading"
        >
          <v-icon left small>mdi-file-pdf-box</v-icon>
          Download PDF
        </v-btn>
      </v-col>
    </v-row>

    <!-- NO DATA STATE -->
    <div v-if="!appLoading && sections.length === 0" class="no-data-box text-center py-8 my-4">
      <v-icon color="#9E6D2B" class="mb-2">mdi-information-outline</v-icon>
      <div class="no-data-text">No stock summary activity found for this date.</div>
    </div>

    <!-- SECTIONS & TABLES -->
    <div v-for="(section, sIdx) in sections" :key="sIdx" class="mb-5">
      <v-card outlined class="rounded-lg overflow-hidden elevation-1" color="#FFFFFF">
        <div class="section-title-bar px-4 py-2.5 font-weight-bold">
          {{ section.title }}
        </div>

        <div class="table-container">
          <table class="stock-table">
            <thead>
              <tr>
                <th class="sticky-name-col">NAME</th>
                <template v-for="col in section.columns">
                  <th :key="col.key + '-qty'" class="text-right" :class="{ 'close-header': col.close }">{{ col.label }} QTY</th>
                  <th :key="col.key + '-wt'" class="text-right" :class="{ 'close-header': col.close }">{{ col.label }} WT</th>
                </template>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, rIdx) in section.rows" :key="rIdx" class="table-row">
                <td class="sticky-name-col name-cell">{{ row.name || 'Uncategorized' }}</td>
                <template v-for="col in section.columns">
                  <td :key="col.key + '-qty'" class="text-right" :class="{ 'close-cell': col.close }">{{ row[col.key + 'Qty'] || 0 }}</td>
                  <td :key="col.key + '-wt'" class="text-right" :class="{ 'close-cell': col.close, 'font-weight-bold': col.close }">{{ (row[col.key + 'Weight'] || 0).toFixed(3) }}</td>
                </template>
              </tr>
            </tbody>
            <tfoot>
              <tr class="totals-row">
                <td class="sticky-name-col total-name-cell">TOTAL</td>
                <template v-for="col in section.columns">
                  <td :key="col.key + '-qty'" class="text-right" :class="{ 'close-cell': col.close }"><strong>{{ section.totals[col.key + 'Qty'] }}</strong></td>
                  <td :key="col.key + '-wt'" class="text-right" :class="{ 'close-cell': col.close }"><strong>{{ section.totals[col.key + 'Weight'].toFixed(3) }}</strong></td>
                </template>
              </tr>
            </tfoot>
          </table>
        </div>
      </v-card>
    </div>
  </div>
</template>

<script>
import axios from "axios";

const STOCK_COLUMNS = [
  { key: "opening", label: "OPEN", always: true },
  { key: "return", label: "RET" },
  { key: "sale", label: "SALE" },
  { key: "exchange", label: "EXCH" },
  { key: "issue", label: "ISSUE" },
  { key: "receipt", label: "RCPT" },
  { key: "closing", label: "CLOSE", always: true, close: true },
];

export default {
  name: "StockSummaryPage",
  data() {
    return {
      appLoading: false,
      ServerError: false,
      showsnackbar: false,
      timeout: 3000,
      msg: null,
      menu: false,
      date: new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
        .toISOString()
        .substr(0, 10),
      sections: [],
    };
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    fetchData() {
      if (!this.date) return;
      this.appLoading = true;
      axios({
        method: "get",
        url: "/data/stocksummary",
        headers: { "x-auth-token": localStorage.getItem("token") },
        params: { date: this.date },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data && response.data.status) {
            const raw = response.data.data;
            this.sections = Object.keys(raw).map((title) => {
              const rows = raw[title];
              const totals = {
                openingQty: 0, openingWeight: 0,
                returnQty: 0, returnWeight: 0,
                saleQty: 0, saleWeight: 0,
                exchangeQty: 0, exchangeWeight: 0,
                issueQty: 0, issueWeight: 0,
                receiptQty: 0, receiptWeight: 0,
                closingQty: 0, closingWeight: 0,
              };
              rows.forEach((r) => {
                Object.keys(totals).forEach((k) => { totals[k] += r[k] || 0; });
              });
              //opening and closing always show; a movement column pair only
              //shows when something actually moved in this section that day
              const columns = STOCK_COLUMNS.filter(
                (c) => c.always || totals[c.key + "Qty"] !== 0 || Math.abs(totals[c.key + "Weight"]) > 0.0005
              );
              return { title, rows, totals, columns };
            });
          } else {
            this.sections = [];
            this.msg = (response.data && response.data.msg) || "Failed to fetch stock data";
            this.showsnackbar = true;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
        });
    },
    downloadPDF() {
      if (!this.date) {
        this.msg = "Please choose a date";
        this.showsnackbar = true;
        return;
      }
      this.appLoading = true;
      axios({
        method: "get",
        url: "/download/stocksummary",
        headers: { "x-auth-token": localStorage.getItem("token") },
        params: { date: this.date },
        responseType: "blob",
      })
        .then((response) => {
          this.appLoading = false;
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", `Stock_Summary_${this.date}.pdf`);
          document.body.appendChild(link);
          link.click();
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
        });
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@600;700&family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap');

.mainHeader {
  font-family: 'Cinzel', serif;
  font-size: 22px;
  font-weight: 700;
  color: #1E1815;
  letter-spacing: 0.3px;
}

.no-data-box {
  background: #FFFFFF;
  border: 1px solid #EFE8DD;
  border-radius: 8px;
}

.no-data-text {
  font-size: 14px;
  color: #8C7D6F;
  font-weight: 500;
}

.section-title-bar {
  background: #FAF6EF;
  color: #1E1815;
  font-size: 14.5px;
  letter-spacing: 0.5px;
  border-bottom: 1px solid #EFE8DD;
}

.table-container {
  overflow-x: auto;
  width: 100%;
}

.stock-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 13px;
  font-family: 'Plus Jakarta Sans', sans-serif;
}

.stock-table th {
  background: #FAF8F5;
  color: #6B5738;
  font-size: 11.5px;
  font-weight: 700;
  padding: 9px 12px;
  vertical-align: middle;
  border-bottom: 1px solid #EFE8DD;
  white-space: nowrap;
  letter-spacing: 0.3px;
}

.stock-table td {
  padding: 8px 12px;
  vertical-align: middle;
  border-bottom: 1px solid #F3EFE8;
  white-space: nowrap;
  color: #1E1815;
}

.table-row:hover td {
  background-color: #FAF5EA !important;
}

.table-row:hover .sticky-name-col {
  background-color: #FAF5EA !important;
}

.sticky-name-col {
  position: sticky;
  left: 0;
  z-index: 2;
  background: #FFFFFF !important;
  border-right: 2px solid #EFE8DD !important;
  font-weight: 600;
  min-width: 130px;
}

th.sticky-name-col {
  background: #FAF8F5 !important;
  color: #6B5738 !important;
}

.name-cell {
  color: #1E1815;
}

.close-header {
  background: #F4ECE0 !important;
  color: #9E6D2B !important;
}

.close-cell {
  background: rgba(158, 109, 43, 0.05);
}

.totals-row td {
  background-color: #F7F2E8 !important;
  border-top: 2px solid #9E6D2B !important;
  border-bottom: none !important;
  color: #1E1815;
  font-weight: 700;
  padding: 10px 12px;
}

.total-name-cell {
  color: #9E6D2B !important;
  background-color: #F7F2E8 !important;
  font-weight: 700;
}
</style>
